import React, { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { collection, doc, getDoc, getDocs } from "firebase/firestore";
import { db } from "../services/firebase";
import { logFirebaseAccessError } from "../utils/firebasePermissionDebug";

export default function Home({ user }) {
  const history = useHistory();
  const [assignedCompanyProjects, setAssignedCompanyProjects] = useState([]);
  const [companyMembers, setCompanyMembers] = useState([]);
  const [assignedProjectsLoading, setAssignedProjectsLoading] = useState(false);
  const [assignedProjectsError, setAssignedProjectsError] = useState("");
  const [accountProfile, setAccountProfile] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const loadCompanyContext = async () => {
      if (!user?.uid) {
        setAssignedCompanyProjects([]);
        setCompanyMembers([]);
        setAccountProfile(null);
        return;
      }

      try {
        setAssignedProjectsLoading(true);
        setAssignedProjectsError("");

        const userSnap = await getDoc(doc(db, "users", user.uid));
        const userData = userSnap.exists() ? userSnap.data() : {};
        const companyId = userData.companyId || "";
        const profile = {
          accountType: userData.accountType || "personal",
          accountBillingType: userData.accountBillingType || "personal",
          billingExempt: Boolean(userData.billingExempt),
          subscriptionRequired: userData.subscriptionRequired !== false,
          role: userData.role || "personal",
          companyId,
          companyName: userData.companyName || "",
          adminDashboardEnabled: Boolean(userData.adminDashboardEnabled),
        };

        if (isMounted) setAccountProfile(profile);

        const isCompanySeat =
          profile.accountBillingType === "companySeat" ||
          profile.billingExempt ||
          !profile.subscriptionRequired;

        if (!isCompanySeat || !companyId) {
          if (isMounted) {
            setAssignedCompanyProjects([]);
            setCompanyMembers([]);
          }
          return;
        }

        const [projectsSnap, membersSnap] = await Promise.all([
          getDocs(collection(db, "companies", companyId, "projects")),
          getDocs(collection(db, "companies", companyId, "members")),
        ]);

        const userEmail = String(userData.email || user.email || "").toLowerCase();
        const members = membersSnap.docs.map((memberDoc) => ({
          id: memberDoc.id,
          ...memberDoc.data(),
        }));
        const projects = projectsSnap.docs.map((projectDoc) => ({
          id: projectDoc.id,
          ...projectDoc.data(),
        }));

        const matchesCurrentUser = (assignment) => {
          if (!assignment) return false;

          if (typeof assignment === "string") {
            const value = assignment.toLowerCase();
            return value === user.uid || value === userEmail;
          }

          if (typeof assignment === "object") {
            const assignmentId = String(
              assignment.id || assignment.uid || assignment.memberId || ""
            ).toLowerCase();
            const assignmentEmail = String(
              assignment.email || assignment.userEmail || ""
            ).toLowerCase();

            return assignmentId === user.uid || assignmentEmail === userEmail;
          }

          return false;
        };

        const assignedProjects = projects.filter((project) => {
          const assignedAsResource = (project.resources || []).some(matchesCurrentUser);
          const assignedTask = (project.tasks || []).some((task) => {
            return (
              matchesCurrentUser(task.assigneeId) ||
              matchesCurrentUser(task.assigneeEmail) ||
              matchesCurrentUser(task.assignee) ||
              matchesCurrentUser(task.assignedTo)
            );
          });

          return assignedAsResource || assignedTask;
        });

        if (isMounted) {
          setCompanyMembers(members);
          setAssignedCompanyProjects(assignedProjects);
        }
      } catch (error) {
        logFirebaseAccessError({
          error,
          path: "users/{uid} or companies/{companyId}/projects|members",
          operation: "read",
          user,
          context: "Home loadCompanyContext",
        });
        console.error("Failed to load company workspace:", error);
        if (isMounted) {
          setAssignedCompanyProjects([]);
          setCompanyMembers([]);
          setAssignedProjectsError(
            "Unable to load assigned company work. Check company workspace permissions."
          );
        }
      } finally {
        if (isMounted) setAssignedProjectsLoading(false);
      }
    };

    loadCompanyContext();

    return () => {
      isMounted = false;
    };
  }, [user]);

  const displayName = user?.displayName || user?.email?.split("@")[0] || "Machinist";
  const isCompanyOwner =
    accountProfile?.accountType === "company" &&
    accountProfile?.role === "owner" &&
    accountProfile?.adminDashboardEnabled;
  const isCompanySeat =
    accountProfile?.accountBillingType === "companySeat" ||
    accountProfile?.billingExempt ||
    accountProfile?.subscriptionRequired === false;

  const calculatorShortcuts = [
    { title: "Feeds, Speeds & RPM", path: "/calculator/feeds-speeds", note: "Dial in cutting data." },
    { title: "G-code", path: "/calculator/g-code", note: "Generate starter machine code." },
    { title: "GD&T Helper", path: "/calculator/gdt", note: "Interpret symbols and tolerance zones." },
    { title: "Tap Drill", path: "/calculator/tap-drill", note: "Thread prep and drill sizes." },
    { title: "Tool Database", path: "/tools", note: "Store tooling, locations, and wear." },
  ];

  const getMemberDisplayName = (member = {}) => {
    return (
      member.username ||
      member.displayName ||
      member.name ||
      String(member.email || "Team Member").split("@")[0] ||
      "Team Member"
    );
  };

  const getAssignmentIdentityValues = (assignment) => {
    if (!assignment) return [];

    if (typeof assignment === "string") return [assignment.toLowerCase()];

    if (typeof assignment === "object") {
      return [
        assignment.id,
        assignment.uid,
        assignment.memberId,
        assignment.email,
        assignment.userEmail,
      ]
        .filter(Boolean)
        .map((value) => String(value).toLowerCase());
    }

    return [];
  };

  const assignedProjectSummaries = useMemo(() => {
    const userEmail = String(user?.email || "").toLowerCase();

    return assignedCompanyProjects.map((project) => {
      const assignedIds = new Set();

      (project.resources || []).forEach((resource) => {
        getAssignmentIdentityValues(resource).forEach((value) => assignedIds.add(value));
      });

      (project.tasks || []).forEach((task) => {
        [task.assigneeId, task.assigneeEmail, task.assignee, task.assignedTo].forEach(
          (assignment) => {
            getAssignmentIdentityValues(assignment).forEach((value) => assignedIds.add(value));
          }
        );
      });

      const assignedMembers = companyMembers.filter((member) => {
        const memberId = String(member.id || member.uid || "").toLowerCase();
        const memberEmail = String(member.email || "").toLowerCase();
        return assignedIds.has(memberId) || assignedIds.has(memberEmail);
      });

      const assignedTasks = (project.tasks || []).filter((task) => {
        const assigneeId = String(task.assigneeId || "").toLowerCase();
        const assigneeEmail = String(task.assigneeEmail || "").toLowerCase();
        return (
          assigneeId === user?.uid ||
          assigneeId === userEmail ||
          assigneeEmail === userEmail ||
          getAssignmentIdentityValues(task.assignee).includes(user?.uid) ||
          getAssignmentIdentityValues(task.assignee).includes(userEmail) ||
          getAssignmentIdentityValues(task.assignedTo).includes(user?.uid) ||
          getAssignmentIdentityValues(task.assignedTo).includes(userEmail)
        );
      });

      return { ...project, assignedMembers, assignedTasks };
    });
  }, [assignedCompanyProjects, companyMembers, user]);

  const openAssignedManufacturingPlan = (project) => {
    if (!project?.id) return;
    const manufacturingPlanId = project.manufacturingPlanId || `company-plan-${project.id}`;
    history.push(`/jobs/${manufacturingPlanId}`);
  };

  return (
    <div style={homePage}>
      <section style={heroCard}>
        <div style={{ maxWidth: "780px" }}>
          <span style={eyebrow}>
            {isCompanySeat ? "Company Workspace" : "Personal Workspace"}
          </span>
          <h1 style={heroTitle}>Welcome back, {displayName}</h1>
          <p style={heroText}>
            ManuCalc now centers the active manufacturing workflow: build plans,
            run calculators, track assigned work, and keep individual and team
            decisions in one place.
          </p>
          <div style={heroActions}>
            <button type="button" style={primaryAction} onClick={() => history.push("/jobs")}>
              Open Manufacturing Plans
            </button>
            <button type="button" style={secondaryAction} onClick={() => history.push("/dashboard")}>
              Open Calculators
            </button>
            {isCompanyOwner && (
              <button type="button" style={secondaryAction} onClick={() => history.push("/admin")}>
                Manage Team
              </button>
            )}
          </div>
        </div>

        <div style={heroStatsGrid}>
          <div style={heroStatCard}>
            <span>Workspace Type</span>
            <strong>{isCompanySeat ? "Business" : "Individual"}</strong>
          </div>
          <div style={heroStatCard}>
            <span>{isCompanySeat ? "Assigned Work" : "Primary Tools"}</span>
            <strong>{isCompanySeat ? assignedCompanyProjects.length : calculatorShortcuts.length}</strong>
          </div>
        </div>
      </section>

      <section style={featureGrid}>
        <button type="button" style={featureCardPrimary} onClick={() => history.push("/jobs")}>
          <span style={featureKicker}>Primary Workflow</span>
          <h2 style={featureTitle}>Manufacturing Plans</h2>
          <p style={featureText}>
            Plan setups, route operations, organize job logic, and move new work
            through the modern ManuCalc workflow.
          </p>
          <strong style={featureLink}>Open planning hub -&gt;</strong>
        </button>

        <div style={featureCard}>
          <span style={featureKicker}>Shop Tools</span>
          <h2 style={featureTitle}>Calculator Shortcuts</h2>
          <div style={shortcutGrid}>
            {calculatorShortcuts.map((item) => (
              <button
                key={item.title}
                type="button"
                style={shortcutButton}
                onClick={() => history.push(item.path)}
              >
                <strong>{item.title}</strong>
                <span>{item.note}</span>
              </button>
            ))}
          </div>
        </div>

        <button type="button" style={featureCard} onClick={() => history.push("/process-builder")}>
          <span style={featureKicker}>Standalone Workflow</span>
          <h2 style={featureTitle}>SOP Process Builder</h2>
          <p style={featureText}>
            Build step-by-step shop procedures with machines, tooling, inspection,
            time, and cost attached to the customer job.
          </p>
          <strong style={featureLink}>Open SOP builder -&gt;</strong>
        </button>

        {isCompanyOwner && (
          <button type="button" style={featureCard} onClick={() => history.push("/admin")}>
            <span style={featureKicker}>Business Admin</span>
            <h2 style={featureTitle}>Team & Seats</h2>
            <p style={featureText}>
              Add employees, invite team members, assign work, and monitor
              business account activity from the admin dashboard.
            </p>
            <strong style={featureLink}>Manage employees -&gt;</strong>
          </button>
        )}
      </section>

      {isCompanySeat && (
        <section style={assignedWorkPanel}>
          <div style={sectionHeader}>
            <div>
              <span style={eyebrow}>Company Assigned Work</span>
              <h2 style={sectionTitle}>Your Team Assignments</h2>
              <p style={sectionText}>
                Work assigned by your company admin appears here. New company
                work should flow through Manufacturing Plans instead of legacy projects.
              </p>
            </div>
            <button type="button" style={secondaryAction} onClick={() => history.push("/jobs")}>
              Open Manufacturing Plans
            </button>
          </div>

          {assignedProjectsLoading ? (
            <div style={emptyBox}>Loading assigned company work...</div>
          ) : assignedProjectsError ? (
            <div style={{ ...emptyBox, color: "#fecaca" }}>{assignedProjectsError}</div>
          ) : assignedProjectSummaries.length === 0 ? (
            <div style={emptyBox}>No team work is assigned yet.</div>
          ) : (
            <div style={assignedProjectGrid}>
              {assignedProjectSummaries.map((project) => (
                <div key={project.id} style={assignedProjectCard}>
                  <div style={{ minWidth: 0 }}>
                    <span style={featureKicker}>Assigned Plan</span>
                    <h3 style={{ margin: "8px 0", color: "var(--text-primary)", fontSize: "1.25rem" }}>
                      {project.name || "Company Manufacturing Plan"}
                    </h3>
                    <p style={projectMeta}>
                      Status: {project.status || "Active"} | Priority: {project.priority || "Medium"}
                    </p>
                    {project.dueDate && <p style={projectMeta}>Due: {project.dueDate}</p>}
                  </div>

                  <div style={assignedInfoBox}>
                    <span>Assigned Team</span>
                    <strong>
                      {project.assignedMembers.length > 0
                        ? project.assignedMembers.map((member) => getMemberDisplayName(member)).join(" / ")
                        : "You"}
                    </strong>
                  </div>

                  <div style={assignedInfoBox}>
                    <span>Your Work</span>
                    <strong>
                      {project.assignedTasks.length > 0
                        ? project.assignedTasks.map((task) => task.title || "Assigned task").join(" / ")
                        : "Assigned as project resource"}
                    </strong>
                  </div>

                  <button
                    type="button"
                    style={primaryAction}
                    onClick={() => openAssignedManufacturingPlan(project)}
                  >
                    Open Assigned Plan
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>
      )}
    </div>
  );
}

const homePage = {
  minHeight: "100vh",
  padding: "32px",
  color: "var(--text-primary)",
  background:
    "linear-gradient(135deg, rgba(85, 182, 215, 0.10), transparent 42%), linear-gradient(180deg, var(--bg-app-start), var(--bg-app-end))",
};

const heroCard = {
  display: "grid",
  gridTemplateColumns: "minmax(0, 1.7fr) minmax(260px, 0.8fr)",
  gap: "24px",
  alignItems: "center",
  maxWidth: "1240px",
  margin: "0 auto 24px",
  padding: "32px",
  borderRadius: "var(--radius-xl)",
  border: "1px solid var(--border-color)",
  background: "var(--bg-surface)",
  boxShadow: "var(--shadow-card)",
};

const eyebrow = {
  display: "inline-flex",
  marginBottom: "10px",
  color: "var(--accent)",
  fontSize: "0.78rem",
  fontWeight: 900,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
};

const heroTitle = {
  margin: 0,
  fontSize: "clamp(2rem, 4vw, 3.8rem)",
  lineHeight: 1,
  fontWeight: 950,
  letterSpacing: 0,
};

const heroText = {
  maxWidth: "760px",
  margin: "16px 0 0",
  color: "var(--text-secondary)",
  fontSize: "1.08rem",
  lineHeight: 1.75,
};

const heroActions = {
  display: "flex",
  flexWrap: "wrap",
  gap: "12px",
  marginTop: "24px",
};

const primaryAction = {
  minHeight: "48px",
  padding: "12px 18px",
  borderRadius: "var(--radius-md)",
  border: "1px solid rgba(85, 182, 215, 0.55)",
  background: "linear-gradient(135deg, var(--accent), var(--accent-strong))",
  color: "#06121a",
  fontWeight: 950,
  fontSize: "14px",
  cursor: "pointer",
  boxShadow: "0 16px 34px rgba(47, 147, 187, 0.22)",
};

const secondaryAction = {
  minHeight: "48px",
  padding: "12px 18px",
  borderRadius: "var(--radius-md)",
  border: "1px solid var(--border-color)",
  background: "var(--bg-panel)",
  color: "var(--text-primary)",
  fontWeight: 850,
  fontSize: "14px",
  cursor: "pointer",
};

const heroStatsGrid = {
  display: "grid",
  gap: "12px",
};

const heroStatCard = {
  display: "grid",
  gap: "8px",
  padding: "18px",
  borderRadius: "var(--radius-lg)",
  border: "1px solid var(--border-color)",
  background: "var(--bg-panel)",
};

const featureGrid = {
  maxWidth: "1240px",
  margin: "0 auto 24px",
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
  gap: "18px",
};

const featureCard = {
  textAlign: "left",
  padding: "24px",
  borderRadius: "var(--radius-lg)",
  border: "1px solid var(--border-color)",
  background: "var(--bg-surface)",
  color: "var(--text-primary)",
  boxShadow: "var(--shadow-card)",
};

const featureCardPrimary = {
  ...featureCard,
  border: "1px solid rgba(85, 182, 215, 0.32)",
  background: "var(--bg-surface)",
  cursor: "pointer",
};

const featureKicker = {
  color: "var(--accent)",
  fontSize: "0.75rem",
  fontWeight: 900,
  letterSpacing: "0.1em",
  textTransform: "uppercase",
};

const featureTitle = {
  margin: "10px 0",
  fontSize: "1.45rem",
  fontWeight: 950,
};

const featureText = {
  color: "var(--text-secondary)",
  lineHeight: 1.65,
};

const featureLink = {
  display: "inline-flex",
  marginTop: "12px",
  color: "var(--accent)",
};

const shortcutGrid = {
  display: "grid",
  gap: "10px",
};

const shortcutButton = {
  display: "grid",
  gap: "4px",
  textAlign: "left",
  padding: "13px",
  borderRadius: "var(--radius-md)",
  border: "1px solid var(--border-color)",
  background: "var(--bg-panel)",
  color: "var(--text-primary)",
  cursor: "pointer",
};

const assignedWorkPanel = {
  maxWidth: "1240px",
  margin: "0 auto",
  padding: "24px",
  borderRadius: "var(--radius-lg)",
  border: "1px solid rgba(85, 182, 215, 0.24)",
  background: "var(--bg-surface)",
  boxShadow: "var(--shadow-card)",
};

const sectionHeader = {
  display: "flex",
  justifyContent: "space-between",
  gap: "16px",
  flexWrap: "wrap",
  alignItems: "flex-start",
  marginBottom: "18px",
};

const sectionTitle = {
  margin: 0,
  fontSize: "1.55rem",
  fontWeight: 950,
};

const sectionText = {
  margin: "8px 0 0",
  color: "var(--text-muted)",
  lineHeight: 1.6,
};

const emptyBox = {
  padding: "28px",
  border: "1px dashed var(--border-color)",
  borderRadius: "var(--radius-lg)",
  textAlign: "center",
  color: "var(--text-muted)",
  background: "var(--bg-panel)",
};

const assignedProjectGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
  gap: "14px",
};

const assignedProjectCard = {
  display: "grid",
  gap: "14px",
  padding: "18px",
  borderRadius: "var(--radius-lg)",
  border: "1px solid var(--border-color)",
  background: "var(--bg-panel)",
};

const assignedInfoBox = {
  display: "grid",
  gap: "5px",
  padding: "13px",
  borderRadius: "var(--radius-md)",
  border: "1px solid var(--border-color)",
  background: "var(--bg-surface-alt)",
  color: "var(--text-secondary)",
};

const projectMeta = {
  margin: "0 0 5px",
  color: "var(--text-muted)",
  fontSize: "0.83rem",
};
